import React, { useState, Suspense, lazy } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { BookOpen, Users, Headphones, MessageSquare, Database, Sparkles, XCircle, Calendar, Target, Check, AlarmClock, Car, Briefcase, Frown, TrendingDown, DollarSign, Moon, Coffee, Smartphone, CheckCircle, ShoppingBag, Wallet, ShieldCheck, Lock, ChevronDown, Gift, X, Clock } from 'lucide-react';
import { useCart } from './CartContext';
const ExitUpsellPopup = lazy(() => import('./ExitUpsellPopup').then(m => ({
  default: m.ExitUpsellPopup
})));
const fadeInUp = {
  hidden: {
    opacity: 0,
    y: 40
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.6
    }
  }
};
export function SalesOfferBottom() {
  const [openFaq, setOpenFaq] = useState<number | null>(null);
  const {
    bump1,
    bump2,
    totalPrice,
    getCartUrl
  } = useCart();
  const [showUpsell, setShowUpsell] = useState(false);
  const toggleFaq = (index: number) => {
    setOpenFaq(openFaq === index ? null : index);
  };
  const handleBottomCTA = (e: React.MouseEvent<HTMLAnchorElement>) => {
    if (window.fbq) {
      window.fbq('track', 'AddToCart', {
        content_name: 'Freelance desde Cero (Sin Inglés)',
        content_type: 'product',
        value: totalPrice,
        currency: 'USD'
      });
    }
    if (!bump1 && !bump2) {
      e.preventDefault();
      setShowUpsell(true);
    }
  };
  return <>
      <Suspense fallback={null} data-id="element-552">
        <ExitUpsellPopup isOpen={showUpsell} onClose={() => setShowUpsell(false)} currentCartUrl={getCartUrl()} data-id="element-553" />
      </Suspense>

      {/* BLOCK 7 — THE 5 ROLES */}
      <section className="py-14 md:py-16 px-4 md:px-6 bg-[#3d2b1f] text-white cv-auto" data-id="element-554">
        <div className="max-w-5xl mx-auto animate-hero-fade-in" data-id="element-555">
          <h2 className="text-3xl md:text-4xl font-bold text-center text-[#faf6f1] mb-10" data-id="element-556">
            Los 5 roles que los negocios hispanos necesitan cubrir hoy
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-557">
            {[{
            num: 'ROL #1',
            icon: <BookOpen className="w-6 h-6 text-[#b8956a]" data-id="element-558" />,
            title: 'Asistente Virtual Administrativa',
            desc: 'Responde correos, confirma citas, organiza agendas. El rol más solicitado en el mercado hispano de EE.UU.'
          }, {
            num: 'ROL #2',
            icon: <Users className="w-6 h-6 text-[#b8956a]" data-id="element-559" />,
            title: 'Moderadora de Redes Sociales',
            desc: 'Responde comentarios, programa publicaciones, mantiene activa la presencia digital del negocio.'
          }, {
            num: 'ROL #3',
            icon: <Headphones className="w-6 h-6 text-[#b8956a]" data-id="element-560" />,
            title: 'Transcriptora de Audio',
            desc: 'Convierte grabaciones en texto. Sin llamadas. Sin reuniones. A tu ritmo.'
          }, {
            num: 'ROL #4',
            icon: <MessageSquare className="w-6 h-6 text-[#b8956a]" data-id="element-561" />,
            title: 'Agente de Atención al Cliente por Chat',
            desc: 'Responde preguntas, resuelve dudas, guía al cliente. Todo por escrito, en español.'
          }, {
            num: 'ROL #5',
            icon: <Database className="w-6 h-6 text-[#b8956a]" data-id="element-562" />,
            title: 'Data Entry Especializado',
            desc: 'Ingresa y organiza información en hojas de cálculo o sistemas simples. Trabajo limpio, tranquilo, predecible.'
          }].map((item, index) => <div key={index} className="bg-[#faf6f1] p-8 rounded-3xl shadow-lg border border-[#b8956a]/20 text-left" data-id="element-563">
                <div className="flex items-center gap-3 mb-4" data-id="element-564">
                  <span className="text-xs font-bold tracking-wider text-[#c9977e] uppercase bg-[#c9977e]/10 px-3 py-1 rounded-full" data-id="element-565">
                    {item.num}
                  </span>
                  {item.icon}
                </div>
                <h3 className="text-xl font-bold text-[#3d2b1f] mb-3" data-id="element-566">
                  {item.title}
                </h3>
                <p className="text-[#6b5744] leading-relaxed" data-id="element-567">{item.desc}</p>
              </div>)}
          </div>
        </div>
      </section>

      {/* BLOCK 6.5 — LO QUE HACE DIFERENTE */}
      <section className="py-14 md:py-16 px-4 md:px-6 bg-[#3d2b1f] text-white overflow-hidden cv-auto" data-id="element-568">
        <div className="max-w-6xl mx-auto animate-hero-fade-in" data-id="element-569">
          <div className="text-center mb-10" data-id="element-570">
            <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-[#b8956a]/20 mb-4" data-id="element-571">
              <Sparkles className="w-6 h-6 text-[#b8956a]" data-id="element-572" />
            </div>
            <h2 className="text-3xl md:text-4xl font-bold text-[#faf6f1] mb-3" data-id="element-573">
              Lo Que Hace Diferente a Este Mapa
            </h2>
            <p className="text-lg text-[#d4b896] font-medium" data-id="element-574">
              No es solo información. Es un sistema completo de ejecución.
            </p>
          </div>

          <div className="flex md:grid md:grid-cols-3 lg:grid-cols-5 gap-5 overflow-x-auto snap-x pb-8 md:pb-0 hide-scrollbar -mx-4 px-4 md:mx-0 md:px-0" data-id="element-575">
            {/* Card 1 */}
            <div className="bg-[#f5e6e0] rounded-3xl p-6 min-w-[280px] md:min-w-0 snap-start flex-shrink-0 flex flex-col" data-id="element-576">
              <div className="bg-white/50 w-12 h-12 rounded-2xl flex items-center justify-center mb-4" data-id="element-577">
                <Users className="w-6 h-6 text-[#c47a6a]" data-id="element-578" />
              </div>
              <h3 className="text-lg font-bold text-[#3d2b1f] mb-3 leading-snug" data-id="element-579">
                5 Historias Reales de Transformación
              </h3>
              <p className="text-[#6b5744] text-sm mb-4 flex-1" data-id="element-580">
                No ejemplos inventados. Lucía, Carmen, Rosa, Valeria y Diana — 5
                mujeres latinas reales con sus roles exactos, sus ciudades y sus
                resultados en días.
              </p>
              <p className="text-[#c47a6a] text-sm italic font-medium" data-id="element-581">
                "Para que te veas a ti misma en cada página."
              </p>
            </div>

            {/* Card 2 */}
            <div className="bg-[#faf6f1] rounded-3xl p-6 min-w-[280px] md:min-w-0 snap-start flex-shrink-0 flex flex-col" data-id="element-582">
              <div className="bg-[#c47a6a]/10 w-12 h-12 rounded-2xl flex items-center justify-center mb-4" data-id="element-583">
                <XCircle className="w-6 h-6 text-[#c47a6a]" data-id="element-584" />
              </div>
              <h3 className="text-lg font-bold text-[#3d2b1f] mb-3 leading-snug" data-id="element-585">
                22 Errores Que No Vas a Cometer
              </h3>
              <p className="text-[#6b5744] text-sm mb-4 flex-1" data-id="element-586">
                Cada capítulo incluye los errores más costosos de las
                principiantes — y la corrección exacta. Porque evitar un error
                vale más que aprender algo nuevo.
              </p>
              <p className="text-[#c47a6a] text-sm italic font-medium" data-id="element-587">
                "Años de experiencia condensados en lo que NO hacer."
              </p>
            </div>

            {/* Card 3 */}
            <div className="bg-[#b8956a]/10 border border-[#b8956a]/20 rounded-3xl p-6 min-w-[280px] md:min-w-0 snap-start flex-shrink-0 flex flex-col" data-id="element-588">
              <div className="bg-[#b8956a]/20 w-12 h-12 rounded-2xl flex items-center justify-center mb-4" data-id="element-589">
                <Calendar className="w-6 h-6 text-[#b8956a]" data-id="element-590" />
              </div>
              <h3 className="text-lg font-bold text-[#faf6f1] mb-3 leading-snug" data-id="element-591">
                Tu Plan de 30 Días Semana a Semana
              </h3>
              <p className="text-[#d4b896] text-sm mb-4 flex-1" data-id="element-592">
                Qué hacer exactamente en tu Semana 1, 2, 3 y 4. Con metas
                realistas, historias de mujeres que las lograron y resultados
                esperados por semana.
              </p>
              <p className="text-[#b8956a] text-sm italic font-medium" data-id="element-593">
                "Sin el 'no sé por dónde empezar'."
              </p>
            </div>

            {/* Card 4 */}
            <div className="bg-[#7a9a6b]/10 border border-[#7a9a6b]/20 rounded-3xl p-6 min-w-[280px] md:min-w-0 snap-start flex-shrink-0 flex flex-col" data-id="element-594">
              <div className="bg-[#7a9a6b]/20 w-12 h-12 rounded-2xl flex items-center justify-center mb-4" data-id="element-595">
                <MessageSquare className="w-6 h-6 text-[#7a9a6b]" data-id="element-596" />
              </div>
              <h3 className="text-lg font-bold text-[#faf6f1] mb-3 leading-snug" data-id="element-597">
                Guiones de Cobro Listos
              </h3>
              <p className="text-[#d4b896] text-sm mb-4 flex-1" data-id="element-598">
                Las palabras exactas para pedir tu primer pago, cobrar
                mensualmente y manejar clientes que retrasan. Porque saber el
                precio no sirve si no sabes cómo pedirlo.
              </p>
              <p className="text-[#7a9a6b] text-sm italic font-medium" data-id="element-599">
                "Cobra con seguridad desde el primer cliente."
              </p>
            </div>

            {/* Card 5 */}
            <div className="bg-[#e0bfb4]/20 border border-[#e0bfb4]/20 rounded-3xl p-6 min-w-[280px] md:min-w-0 snap-start flex-shrink-0 flex flex-col" data-id="element-600">
              <div className="bg-[#e0bfb4]/30 w-12 h-12 rounded-2xl flex items-center justify-center mb-4" data-id="element-601">
                <Target className="w-6 h-6 text-[#e0bfb4]" data-id="element-602" />
              </div>
              <h3 className="text-lg font-bold text-[#faf6f1] mb-3 leading-snug" data-id="element-603">
                Test de Rol Incluido
              </h3>
              <p className="text-[#d4b896] text-sm mb-4 flex-1" data-id="element-604">
                5 preguntas que en 2 minutos te dicen exactamente cuál de los 5
                roles es perfecto para tu personalidad. No tienes que adivinar —
                el mapa te lo dice.
              </p>
              <p className="text-[#e0bfb4] text-sm italic font-medium" data-id="element-605">
                "Empieza donde tienes más probabilidad de éxito."
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* BLOCK 3 — AGITACIÓN DE DOLOR */}
      <section className="py-14 md:py-16 px-4 md:px-6 bg-[#3d2b1f] text-white relative overflow-hidden cv-auto" data-id="element-606">
        <div className="absolute top-0 right-0 w-64 h-64 bg-[#b8956a] opacity-10 rounded-full hidden md:block blur-3xl translate-x-1/2 -translate-y-1/2" data-id="element-607"></div>
        <div className="max-w-3xl mx-auto text-center relative z-10 animate-hero-fade-in" data-id="element-608">
          <h2 className="text-3xl md:text-4xl font-bold mb-8 text-[#faf6f1]" data-id="element-609">
            Voy a ser directa contigo. Porque tú te mereces eso.
          </h2>
          <div className="space-y-5 text-lg md:text-xl text-[#d4b896] leading-relaxed text-left max-w-2xl mx-auto" data-id="element-610">
            <p data-id="element-611">No te voy a decir que esto es fácil.</p>
            <p data-id="element-612">
              No te voy a decir que mañana vas a despertar siendo millonaria.
            </p>
            <p data-id="element-613">Y no te voy a prometer que no vas a tener que esforzarte.</p>
            <p className="text-[#faf6f1] font-medium pt-2" data-id="element-614">
              Lo que sí te voy a decir es esto:
            </p>
            <p data-id="element-615">
              El esfuerzo que ya estás poniendo en tu trabajo físico — ese mismo
              nivel de compromiso — aplicado al mundo digital, te puede dar el
              doble de resultado en la mitad del tiempo.
            </p>
            <p data-id="element-616">
              Porque el trabajo digital no te pide tu espalda. No te pide tus
              rodillas. No te pide que sonrías cuando estás agotada.
            </p>
            <p data-id="element-617">Solo te pide tu atención, tu amabilidad y tu español.</p>
            <p className="text-[#faf6f1] font-bold" data-id="element-618">Y eso... ya lo tienes.</p>
            <div className="mt-8 p-6 bg-[#6b5744]/20 rounded-3xl border border-[#b8956a]/20 text-center" data-id="element-619">
              <p className="text-[#faf6f1] mb-2" data-id="element-620">
                Lo que te ha faltado no es la habilidad.
              </p>
              <p className="text-[#faf6f1] mb-3" data-id="element-621">
                Lo que te ha faltado es el mapa.
              </p>
              <p className="text-2xl font-bold text-[#b8956a]" data-id="element-622">
                Eso es exactamente lo que Freelance Desde Cero te da.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* BLOCK 5 — MECANISMO ÚNICO */}
      <section className="py-14 md:py-16 px-4 md:px-6 bg-[#3d2b1f] text-white relative overflow-hidden cv-auto" data-id="element-623">
        <div className="absolute bottom-0 left-0 w-80 h-80 bg-[#c9977e] opacity-10 rounded-full hidden md:block blur-[120px] -translate-x-1/2 translate-y-1/2" data-id="element-624"></div>
        <div className="max-w-3xl mx-auto relative z-10 animate-hero-fade-in" data-id="element-625">
          <div className="text-center mb-8" data-id="element-626">
            <h2 className="text-3xl md:text-4xl font-bold mb-4 leading-tight text-[#faf6f1]" data-id="element-627">
              En muchos trabajos físicos te pagan ~$12/h. Online, este tipo de
              ayuda se cobra $25/h o más.
            </h2>
            <p className="text-[#b8956a] text-xl md:text-2xl font-medium mt-4" data-id="element-628">
              Porque para un negocio, un mensaje no respondido = una venta
              perdida. (Esto se llama El Arbitraje de Idioma.)
            </p>
          </div>

          <div className="space-y-5 text-lg text-[#d4b896] leading-relaxed" data-id="element-629">
            <p data-id="element-630">
              Hay miles de negocios latinos en EE.UU. perdiendo ventas ahora
              mismo por no responder mensajes.
            </p>
            <p data-id="element-631">
              Los negocios de dueños latinos están creciendo al doble de
              velocidad que el promedio americano. Restaurantes, boutiques,
              salones, barberías, clínicas dentales, servicios de mudanza. Todos
              creciendo.
            </p>
            <p className="text-[#faf6f1] font-bold" data-id="element-632">
              Pero sus dueños tienen un problema enorme que nadie habla:
            </p>
            <p data-id="element-633">
              No tienen tiempo para responder sus mensajes. Su Instagram tiene
              200 mensajes sin contestar. Su WhatsApp suena todo el día. Sus
              clientes preguntan precios, hacen citas, piden información — y
              nadie responde.
            </p>
            <p className="text-[#faf6f1] font-medium" data-id="element-634">
              Cada mensaje sin respuesta es una venta perdida. Y ellos lo saben.
            </p>
            <p data-id="element-635">Por eso están buscando activamente a alguien que:</p>

            <ul className="space-y-3 my-6" data-id="element-636">
              {['Les sale más barato que un empleado de tiempo completo', 'Hable español naturalmente (no traducido)', 'Entienda la cultura latina (el tono, el respeto, la calidez)', 'Pueda trabajar de forma remota y flexible'].map((item, i) => <li key={i} className="flex items-start gap-3 text-[#faf6f1]" data-id="element-637">
                  <Check className="w-6 h-6 text-[#b8956a] flex-shrink-0" data-id="element-638" />
                  <span data-id="element-639">{item}</span>
                </li>)}
            </ul>

            <p data-id="element-640">
              No necesitan a alguien con un MBA. Necesitan a alguien que sepa
              tratar bien a la gente en español.
            </p>

            {/* Mini-caso concreto */}
            <div className="mt-6 p-5 bg-[#b8956a]/10 rounded-2xl border border-[#b8956a]/20" data-id="element-641">
              <p className="text-base text-[#faf6f1] font-bold mb-1" data-id="element-642">
                Ejemplo real:
              </p>
              <p className="text-[#d4b896] text-base leading-relaxed" data-id="element-643">
                Barbería latina → 40 mensajes/día sin responder → tú respondes
                en 10 min → agendas 3 citas/día → te pagan semanal. Así de
                simple.
              </p>
            </div>

            <div className="mt-8 p-6 bg-[#faf6f1] rounded-3xl text-center shadow-2xl" data-id="element-644">
              <p className="text-xl text-[#3d2b1f] font-bold mb-3" data-id="element-645">
                Esa persona eres tú.
              </p>
              <p className="text-lg text-[#6b5744] mb-4" data-id="element-646">
                Eso es el Arbitraje de Idioma: tu español vale mucho más en el
                mercado digital que en el físico — porque cada mensaje que
                respondes le genera ventas a un negocio.
              </p>
              <p className="text-2xl font-bold text-[#b8956a]" data-id="element-647">
                La brecha existe. La oportunidad existe.
                <br data-id="element-648" />
                Freelance Desde Cero te da el puente.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* BLOCK 5.5 — TU DÍA TIPO */}
      <section className="py-14 md:py-16 px-4 md:px-6 bg-[#faf6f1] cv-auto" data-id="element-649">
        <div className="max-w-5xl mx-auto animate-hero-fade-in" data-id="element-650">
          <div className="text-center mb-10" data-id="element-651">
            <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-[#b8956a]/20 mb-4" data-id="element-652">
              <Clock className="w-6 h-6 text-[#b8956a]" data-id="element-653" />
            </div>
            <h2 className="text-3xl md:text-4xl font-bold text-[#3d2b1f] mb-3" data-id="element-654">
              Cómo Se Ve Tu Vida Con Este Mapa
            </h2>
            <p className="text-lg text-[#6b5744] font-medium max-w-2xl mx-auto" data-id="element-655">
              Esto no es motivación. Es el horario real de una mujer que empezó
              freelance desde cero (sin inglés) y ya tiene 2 clientes.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8" data-id="element-656">
            {/* Sin el Mapa */}
            <div className="bg-white rounded-3xl p-6 shadow-lg border-2 border-[#c47a6a]/20" data-id="element-657">
              <h3 className="text-xl font-bold text-[#c47a6a] mb-6 text-center border-b border-[#c47a6a]/20 pb-4" data-id="element-658">
                Sin el Mapa
              </h3>
              <div className="space-y-5" data-id="element-659">
                {[{
                time: '5:00 AM',
                text: 'Suena la alarma → otro día igual',
                icon: <AlarmClock className="w-5 h-5 text-[#c47a6a]" data-id="element-660" />
              }, {
                time: '6:30 AM',
                text: 'Tráfico, frío, jefes esperando',
                icon: <Car className="w-5 h-5 text-[#c47a6a]" data-id="element-661" />
              }, {
                time: '8:00 AM',
                text: 'Turno agotador sin parar',
                icon: <Briefcase className="w-5 h-5 text-[#c47a6a]" data-id="element-662" />
              }, {
                time: '10:00 AM',
                text: 'Cero energía, cero tiempo propio',
                icon: <Frown className="w-5 h-5 text-[#c47a6a]" data-id="element-663" />
              }, {
                time: '3:00 PM',
                text: 'Llegas a casa drenada',
                icon: <TrendingDown className="w-5 h-5 text-[#c47a6a]" data-id="element-664" />
              }, {
                time: '10:00 PM',
                text: 'Ingresos que no compensan el desgaste',
                icon: <DollarSign className="w-5 h-5 text-[#c47a6a]" data-id="element-665" />
              }].map((item, i) => <div key={i} className="flex items-center gap-4 bg-[#c47a6a]/5 p-3 rounded-xl" data-id="element-666">
                    <div className="w-20 flex-shrink-0 font-bold text-[#3d2b1f] text-sm" data-id="element-667">
                      {item.time}
                    </div>
                    <div className="flex-shrink-0" data-id="element-668">{item.icon}</div>
                    <div className="text-sm text-[#6b5744] font-medium" data-id="element-669">
                      {item.text}
                    </div>
                  </div>)}
              </div>
            </div>

            {/* Con el Mapa */}
            <div className="bg-white rounded-3xl p-6 shadow-xl border-2 border-[#7a9a6b]/30 relative overflow-hidden" data-id="element-670">
              <div className="absolute top-0 right-0 w-32 h-32 bg-[#7a9a6b]/10 rounded-full hidden md:block blur-2xl -translate-y-1/2 translate-x-1/2" data-id="element-671"></div>
              <h3 className="text-xl font-bold text-[#7a9a6b] mb-6 text-center border-b border-[#7a9a6b]/20 pb-4 relative z-10" data-id="element-672">
                Con el Mapa
              </h3>
              <div className="space-y-5 relative z-10" data-id="element-673">
                {[{
                time: '5:00 AM',
                text: 'Todavía dormida',
                icon: <Moon className="w-5 h-5 text-[#7a9a6b]" data-id="element-674" />
              }, {
                time: '6:30 AM',
                text: 'Café en tu cocina, sin prisa',
                icon: <Coffee className="w-5 h-5 text-[#7a9a6b]" data-id="element-675" />
              }, {
                time: '8:00 AM',
                text: 'Revisas mensajes (20 min) → próximos pasos listos',
                icon: <Smartphone className="w-5 h-5 text-[#7a9a6b]" data-id="element-676" />
              }, {
                time: '10:00 AM',
                text: 'Envías 2 propuestas → 1 llamada agendada',
                icon: <CheckCircle className="w-5 h-5 text-[#7a9a6b]" data-id="element-677" />
              }, {
                time: '3:00 PM',
                text: 'Libre para ti, tu familia, lo que quieras',
                icon: <ShoppingBag className="w-5 h-5 text-[#7a9a6b]" data-id="element-678" />
              }, {
                time: '10:00 PM',
                text: 'Ingresos online sin turno físico (rumbo a $1,000/mes)',
                icon: <Wallet className="w-5 h-5 text-[#7a9a6b]" data-id="element-679" />
              }].map((item, i) => <div key={i} className="flex items-center gap-4 bg-[#7a9a6b]/5 p-3 rounded-xl" data-id="element-680">
                    <div className="w-20 flex-shrink-0 font-bold text-[#3d2b1f] text-sm" data-id="element-681">
                      {item.time}
                    </div>
                    <div className="flex-shrink-0" data-id="element-682">{item.icon}</div>
                    <div className="text-sm text-[#3d2b1f] font-bold" data-id="element-683">
                      {item.text}
                    </div>
                  </div>)}
              </div>
            </div>
          </div>

          <div className="bg-[#b8956a]/10 rounded-2xl p-6 text-center max-w-2xl mx-auto border border-[#b8956a]/20" data-id="element-684">
            <p className="text-lg md:text-xl font-bold text-[#3d2b1f] mb-2" data-id="element-685">
              La diferencia no es el esfuerzo. Es el mercado donde aplicas ese
              esfuerzo.
            </p>
            <p className="text-sm md:text-base text-[#6b5744] font-medium" data-id="element-686">
              El mapa te dice exactamente: qué vender, dónde conseguir clientes
              y qué hacer cada día (sin inglés).
            </p>
          </div>
        </div>
      </section>

      {/* BLOCK 8 — PROOF */}
      <section className="py-10 md:py-12 px-4 md:px-6 bg-[#faf6f1] cv-auto" data-id="element-687">
        <div className="max-w-3xl mx-auto text-center animate-hero-fade-in" data-id="element-688">
          <h2 className="text-3xl md:text-4xl font-bold text-[#3d2b1f] mb-5" data-id="element-689">
            ¿Por qué esto funciona?
          </h2>
          <div className="space-y-4 text-lg md:text-xl text-[#6b5744] leading-relaxed" data-id="element-690">
            <p data-id="element-691">
              El mercado hispano en EE.UU. genera más de{' '}
              <span className="font-bold text-[#b8956a]" data-id="element-692">$3.2 trillones</span>{' '}
              en actividad económica anual.
            </p>
            <p data-id="element-693">
              Son más de{' '}
              <span className="font-bold text-[#b8956a]" data-id="element-694">5 millones</span> de
              negocios operados por latinos en todo el país.
            </p>
            <p data-id="element-695">
              La mayoría de ellos tiene presencia digital incompleta, mensajes
              sin responder y operaciones caóticas porque no encuentran ayuda en
              su idioma.
            </p>
            <div className="mt-5 p-6 bg-[#e0bfb4]/20 rounded-2xl" data-id="element-696">
              <p className="text-2xl font-bold text-[#3d2b1f]" data-id="element-697">
                No es una tendencia. Es una brecha enorme.
              </p>
              <p className="text-xl font-medium text-[#c9977e] mt-2" data-id="element-698">
                Y tú puedes llenarla.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* BLOCK 9 — WHO IT'S FOR */}
      <section className="py-14 md:py-16 px-4 md:px-6 bg-[#3d2b1f] text-white cv-auto" data-id="element-699">
        <div className="max-w-5xl mx-auto animate-hero-fade-in" data-id="element-700">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-12" data-id="element-701">
            {/* FOR YOU */}
            <div className="bg-[#faf6f1] p-8 md:p-10 rounded-3xl shadow-xl" data-id="element-702">
              <h3 className="text-2xl font-bold mb-8 text-[#3d2b1f]" data-id="element-703">
                Este libro es para ti si...
              </h3>
              <ul className="space-y-5" data-id="element-704">
                {['Llevas años en trabajos físicos y tu cuerpo ya está sintiendo el peso', 'Hablas español fluido pero nadie te dijo que eso tiene valor digital', 'Has intentado otras cosas online y ninguna funcionó porque no eran para tu realidad', 'Eres organizada, responsable y buscas algo que puedas hacer desde casa', 'No tienes experiencia digital pero tienes disposición para aprender lo básico'].map((item, i) => <li key={i} className="flex items-start gap-3" data-id="element-705">
                    <div className="bg-[#7a9a6b]/20 p-1 rounded-full flex-shrink-0 mt-1" data-id="element-706">
                      <Check className="w-4 h-4 text-[#7a9a6b]" data-id="element-707" />
                    </div>
                    <span className="text-[#6b5744]" data-id="element-708">{item}</span>
                  </li>)}
              </ul>
            </div>

            {/* NOT FOR YOU */}
            <div className="bg-[#faf6f1] p-8 md:p-10 rounded-3xl shadow-xl opacity-90" data-id="element-709">
              <h3 className="text-2xl font-bold mb-8 text-[#3d2b1f]" data-id="element-710">
                Este libro NO es para ti si...
              </h3>
              <ul className="space-y-5" data-id="element-711">
                {['Buscas hacerte rica de la noche a la mañana sin esfuerzo', 'No estás dispuesta a seguir un proceso paso a paso', 'Ya tienes ingresos digitales estables y solo buscas escalar'].map((item, i) => <li key={i} className="flex items-start gap-3" data-id="element-712">
                    <div className="bg-[#c47a6a]/20 p-1 rounded-full flex-shrink-0 mt-1" data-id="element-713">
                      <X className="w-4 h-4 text-[#c47a6a]" data-id="element-714" />
                    </div>
                    <span className="text-[#6b5744]" data-id="element-715">{item}</span>
                  </li>)}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* BLOCK 10 — RISK REVERSAL */}
      <section className="py-12 md:py-14 px-4 md:px-6 bg-[#faf6f1] cv-auto" data-id="element-716">
        <div className="max-w-4xl mx-auto text-center animate-hero-fade-in" data-id="element-717">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-[#b8956a]/20 mb-6" data-id="element-718">
            <ShieldCheck className="w-8 h-8 text-[#b8956a]" data-id="element-719" />
          </div>
          <h2 className="text-3xl md:text-4xl font-bold text-[#3d2b1f] mb-8" data-id="element-720">
            Mi Garantía de 7 Días
          </h2>

          <div className="max-w-2xl mx-auto mb-8 text-left" data-id="element-721">
            <div className="bg-[#7a9a6b]/5 border-2 border-[#7a9a6b]/20 rounded-3xl p-6 md:p-8" data-id="element-722">
              <h3 className="text-xl font-bold text-[#7a9a6b] mb-4" data-id="element-723">
                Garantía de Satisfacción Total — 7 Días
              </h3>
              <p className="text-[#6b5744] leading-relaxed" data-id="element-724">
                Si en 7 días sientes que este mapa no es para ti — por cualquier
                razón — te devuelvo tus $14 completos. Sin formularios. Sin
                preguntas. Sin drama. Solo escríbeme y listo.
              </p>
            </div>
          </div>

          <div className="bg-[#b8956a]/10 border border-[#b8956a]/30 rounded-2xl p-6 flex flex-col sm:flex-row items-center justify-center gap-4 max-w-2xl mx-auto" data-id="element-725">
            <Lock className="w-8 h-8 text-[#b8956a] flex-shrink-0" data-id="element-726" />
            <p className="text-lg font-bold text-[#3d2b1f] text-left" data-id="element-727">
              "Riesgo cero para ti. Todo el riesgo es mío. Así de segura estoy
              de que este mapa va a cambiar algo para ti."
            </p>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-12 md:py-14 px-4 md:px-6 bg-[#faf6f1]" data-id="element-728">
        <div className="max-w-3xl mx-auto animate-hero-fade-in" data-id="element-729">
          <h2 className="text-3xl font-bold text-center text-[#3d2b1f] mb-8" data-id="element-730">
            Preguntas Frecuentes
          </h2>

          <div className="space-y-4" data-id="element-731">
            {[{
            q: '¿Necesito hablar inglés?',
            a: 'Absolutamente no. Este mapa fue construido específicamente para trabajar en el mercado latino de EE.UU. — donde tu español es la ventaja, no el problema. Todas las instrucciones, scripts y estrategias están en español. Tus clientes hablan español. Tu trabajo es en español.'
          }, {
            q: '¿Necesito experiencia previa?',
            a: 'No. Si sabes enviar un WhatsApp y organizar algo (una lista, una agenda, unos archivos), tienes el 70% de lo que necesitas. El 30% restante lo aprendes en las primeras 48 horas de leer el mapa.'
          }, {
            q: '¿Necesito una computadora?',
            a: 'No. El 90% de las mujeres que usan este mapa empezaron solo con su teléfono. Google Calendar, WhatsApp Business, Google Sheets — todo funciona desde tu celular.'
          }, {
            q: '¿En cuánto tiempo puedo tener mi primer cliente?',
            a: 'El promedio de las mujeres que siguen el Plan de 7 Días incluido en los bonos es entre 7 y 14 días. Algunas lo logran antes. Depende de cuántos mensajes envíes y con qué consistencia apliques el método.'
          }, {
            q: '¿Qué pasa si no me gusta?',
            a: 'Tienes 7 días completos para revisarlo todo. Si no es para ti — por cualquier razón — te devuelvo tus $14 sin preguntas. No hay riesgo para ti. Solo para mí.'
          }, {
            q: '¿Cómo recibo el material?',
            a: 'Inmediatamente después del pago recibes un email con acceso a todo: el mapa principal y los 5 bonos. Puedes empezar a leer en los próximos 5 minutos.'
          }, {
            q: '¿Es un pago único?',
            a: 'Sí. $14 una sola vez. Sin suscripciones, sin mensualidades, sin sorpresas. Pagas hoy y el material es tuyo para siempre.'
          }, {
            q: '¿Para hombres también funciona?',
            a: 'Sí. Aunque el lenguaje está dirigido principalmente a mujeres latinas (que son mi audiencia principal), el sistema, los scripts y las estrategias funcionan para cualquier persona hispanohablante que quiera empezar como freelancer.'
          }].map((item, index) => <div key={index} className="bg-white rounded-2xl shadow-sm border border-[#e0bfb4]/30 overflow-hidden" data-id="element-732">
                <button onClick={() => toggleFaq(index)} className="w-full px-6 py-5 text-left flex items-center justify-between hover:bg-[#faf6f1] transition-colors" data-id="element-733">
                  <span className="font-bold text-[#3d2b1f] text-lg pr-4" data-id="element-734">
                    {item.q}
                  </span>
                  <ChevronDown className={`w-5 h-5 text-[#b8956a] flex-shrink-0 transition-transform duration-300 ${openFaq === index ? 'rotate-180' : ''}`} data-id="element-735" />
                </button>
                <AnimatePresence data-id="element-736">
                  {openFaq === index && <motion.div initial={{
                height: 0,
                opacity: 0
              }} animate={{
                height: 'auto',
                opacity: 1
              }} exit={{
                height: 0,
                opacity: 0
              }} transition={{
                duration: 0.3
              }} data-id="element-737">
                      <div className="px-6 pb-6 text-[#6b5744]" data-id="element-738">{item.a}</div>
                    </motion.div>}
                </AnimatePresence>
              </div>)}
          </div>
        </div>
      </section>

      {/* BONUSES SECTION */}
      <section className="py-14 md:py-16 px-4 md:px-6 bg-[#3d2b1f] text-white" data-id="element-739">
        <div className="max-w-5xl mx-auto animate-hero-fade-in" data-id="element-740">
          <div className="text-center mb-12" data-id="element-741">
            <p className="text-xl md:text-2xl font-bold text-[#faf6f1] mb-4" data-id="element-742">
              PERO ESO NO ES TODO.
            </p>
            <p className="text-lg text-[#d4b896] max-w-2xl mx-auto" data-id="element-743">
              Cuando compras hoy, recibes 5 Bonos Especiales diseñados para que
              no solo aprendas... sino que ejecutes desde el primer día.
            </p>
          </div>

          <div className="space-y-10" data-id="element-744">
            {/* BONO 1 */}
            <div className="bg-[#faf6f1] rounded-3xl overflow-hidden shadow-xl border border-[#b8956a]/20" data-id="element-745">
              <div className="flex flex-col md:flex-row" data-id="element-746">
                <div className="md:w-1/3 p-6 flex items-center justify-center bg-[#f5e6e0]" data-id="element-747">
                  <img src="https://cdn.magicpatterns.com/uploads/tj4w3dzjdE8gLhXHBdBFWk/BONO_1.png" alt="Bono 1 - El Plan 7 Días Primer Dinero" className="w-full max-w-[200px] h-auto rounded-xl shadow-lg" loading="lazy" decoding="async" data-id="element-748" />
                </div>
                <div className="md:w-2/3 p-6 md:p-8" data-id="element-749">
                  <div className="flex items-center gap-3 mb-3" data-id="element-750">
                    <div className="bg-[#b8956a]/10 p-2 rounded-full" data-id="element-751">
                      <Gift className="w-5 h-5 text-[#b8956a]" data-id="element-752" />
                    </div>
                    <span className="text-xs font-bold tracking-wider text-[#c9977e] uppercase bg-[#c9977e]/10 px-3 py-1 rounded-full" data-id="element-753">
                      BONO #1
                    </span>
                  </div>
                  <h3 className="text-xl md:text-2xl font-bold text-[#3d2b1f] mb-2" data-id="element-754">
                    EL PLAN 7 DÍAS "PRIMER DINERO" (SIN INGLÉS)
                  </h3>
                  <div className="flex items-center gap-3 mb-4" data-id="element-755">
                    <span className="bg-[#7a9a6b] text-white text-xs font-bold px-3 py-1 rounded-full" data-id="element-756">
                      INCLUIDO HOY
                    </span>
                    <span className="text-[#9a8b7a] text-xs" data-id="element-757">
                      (Precio regular: $9)
                    </span>
                  </div>
                  <p className="text-[#6b5744] mb-4" data-id="element-758">
                    Un plan día a día (10–20 min/día) para que en 7 días tengas:
                  </p>
                  <ul className="space-y-2 mb-4" data-id="element-759">
                    {['20–50 negocios contactados', 'Conversaciones activas reales', 'Mínimo 1 propuesta enviada'].map((item, i) => <li key={i} className="flex items-center gap-2 text-[#3d2b1f]" data-id="element-760">
                        <Check className="w-4 h-4 text-[#7a9a6b] flex-shrink-0" data-id="element-761" />
                        <span className="font-medium" data-id="element-762">{item}</span>
                      </li>)}
                  </ul>
                  <p className="text-sm text-[#6b5744] mb-1" data-id="element-763">
                    Incluye: Plan acción Día 1 al Día 7 con checklist diaria,
                    scripts para DM, WhatsApp y Facebook, seguimientos que no
                    molestan, y tracker de control.
                  </p>
                  <p className="text-[#c9977e] italic font-medium mt-3" data-id="element-764">
                    "Ya no tendrás excusa de 'no sé por dónde empezar.'"
                  </p>
                </div>
              </div>
            </div>

            {/* BONO 2 */}
            <div className="bg-[#faf6f1] rounded-3xl overflow-hidden shadow-xl border border-[#b8956a]/20" data-id="element-765">
              <div className="flex flex-col md:flex-row" data-id="element-766">
                <div className="md:w-1/3 p-6 flex items-center justify-center bg-[#f5e6e0]" data-id="element-767">
                  <img src="https://cdn.magicpatterns.com/uploads/tiRmjyzmVTm5Ais22gdmbP/BONO_2.png" alt="Bono 2 - 50 Mensajes Copia y Pega" className="w-full max-w-[200px] h-auto rounded-xl shadow-lg" loading="lazy" decoding="async" data-id="element-768" />
                </div>
                <div className="md:w-2/3 p-6 md:p-8" data-id="element-769">
                  <div className="flex items-center gap-3 mb-3" data-id="element-770">
                    <div className="bg-[#b8956a]/10 p-2 rounded-full" data-id="element-771">
                      <Gift className="w-5 h-5 text-[#b8956a]" data-id="element-772" />
                    </div>
                    <span className="text-xs font-bold tracking-wider text-[#c9977e] uppercase bg-[#c9977e]/10 px-3 py-1 rounded-full" data-id="element-773">
                      BONO #2
                    </span>
                  </div>
                  <h3 className="text-xl md:text-2xl font-bold text-[#3d2b1f] mb-2" data-id="element-774">
                    50 MENSAJES "COPIA Y PEGA" PARA CONSEGUIR CLIENTES EN
                    ESPAÑOL (SIN VERTE ROGANDO)
                  </h3>
                  <div className="flex items-center gap-3 mb-4" data-id="element-775">
                    <span className="bg-[#7a9a6b] text-white text-xs font-bold px-3 py-1 rounded-full" data-id="element-776">
                      INCLUIDO HOY
                    </span>
                    <span className="text-[#9a8b7a] text-xs" data-id="element-777">
                      (Precio regular: $14)
                    </span>
                  </div>
                  <p className="text-[#6b5744] mb-4" data-id="element-778">
                    50 mensajes listos para enviar HOY por IG, WhatsApp y
                    Facebook.
                  </p>
                  <ul className="space-y-2 mb-4" data-id="element-779">
                    {['15 scripts para Instagram DM', '15 scripts para WhatsApp', '10 scripts para Facebook Page', 'Scripts por industria (Beauty, Dental, Seguros, Restaurantes)', '5 seguimientos que no se sienten intensos', '8 respuestas a objeciones ("¿cuánto cobras?" / "ya tengo a alguien" / "ahora no")'].map((item, i) => <li key={i} className="flex items-start gap-2 text-[#3d2b1f]" data-id="element-780">
                        <Check className="w-4 h-4 text-[#7a9a6b] flex-shrink-0 mt-0.5" data-id="element-781" />
                        <span className="font-medium" data-id="element-782">{item}</span>
                      </li>)}
                  </ul>
                  <p className="text-[#c9977e] italic font-medium mt-3" data-id="element-783">
                    "Solo cambiás el nombre y enviás. Así de simple."
                  </p>
                </div>
              </div>
            </div>

            {/* BONO 3 */}
            <div className="bg-[#faf6f1] rounded-3xl overflow-hidden shadow-xl border border-[#b8956a]/20" data-id="element-784">
              <div className="flex flex-col md:flex-row" data-id="element-785">
                <div className="md:w-1/3 p-6 flex items-center justify-center bg-[#f5e6e0]" data-id="element-786">
                  <img src="https://cdn.magicpatterns.com/uploads/e5tvQA8W7xvbVBQ4GrAQ3i/BONO_3.png" alt="Bono 3 - Calculadora Express de Precios + Paquetes" className="w-full max-w-[200px] h-auto rounded-xl shadow-lg" loading="lazy" decoding="async" data-id="element-787" />
                </div>
                <div className="md:w-2/3 p-6 md:p-8" data-id="element-788">
                  <div className="flex items-center gap-3 mb-3" data-id="element-789">
                    <div className="bg-[#b8956a]/10 p-2 rounded-full" data-id="element-790">
                      <Gift className="w-5 h-5 text-[#b8956a]" data-id="element-791" />
                    </div>
                    <span className="text-xs font-bold tracking-wider text-[#c9977e] uppercase bg-[#c9977e]/10 px-3 py-1 rounded-full" data-id="element-792">
                      BONO #3
                    </span>
                  </div>
                  <h3 className="text-xl md:text-2xl font-bold text-[#3d2b1f] mb-2" data-id="element-793">
                    CALCULADORA EXPRESS DE PRECIOS + PAQUETES (PARA COBRAR SIN
                    PENA)
                  </h3>
                  <div className="flex items-center gap-3 mb-4" data-id="element-794">
                    <span className="bg-[#7a9a6b] text-white text-xs font-bold px-3 py-1 rounded-full" data-id="element-795">
                      INCLUIDO HOY
                    </span>
                    <span className="text-[#9a8b7a] text-xs" data-id="element-796">
                      (Precio regular: $14)
                    </span>
                  </div>
                  <p className="text-[#6b5744] mb-4" data-id="element-797">En 10 minutos tendrás:</p>
                  <ul className="space-y-2 mb-4" data-id="element-798">
                    {['Tu precio base calculado (fórmula de 3 pasos)', '3 paquetes listos (Básico / Estándar / Premium)', 'Scripts para enviar tu precio por WhatsApp sin nervios', 'Respuestas a "está caro" y "dame descuento"', 'Checklist para detectar clientes problemáticos antes de empezar', 'Mini acuerdo por WhatsApp (sin contratos ni abogados)'].map((item, i) => <li key={i} className="flex items-start gap-2 text-[#3d2b1f]" data-id="element-799">
                        <Check className="w-4 h-4 text-[#7a9a6b] flex-shrink-0 mt-0.5" data-id="element-800" />
                        <span className="font-medium" data-id="element-801">{item}</span>
                      </li>)}
                  </ul>
                  <p className="text-[#c9977e] italic font-medium mt-3" data-id="element-802">
                    "Porque si no sabés cuánto cobrar, terminás aceptando
                    cualquier cosa."
                  </p>
                </div>
              </div>
            </div>

            {/* BONO 4 */}
            <div className="bg-[#faf6f1] rounded-3xl overflow-hidden shadow-xl border border-[#b8956a]/20" data-id="element-803">
              <div className="flex flex-col md:flex-row" data-id="element-804">
                <div className="md:w-1/3 p-6 flex items-center justify-center bg-[#f5e6e0]" data-id="element-805">
                  <img src="https://cdn.magicpatterns.com/uploads/fnGJQCMUkiEYeP2MZX7c4R/BONO_4.png" alt="Bono 4 - La Lista Secreta: 50 Negocios" className="w-full max-w-[200px] h-auto rounded-xl shadow-lg" loading="lazy" decoding="async" data-id="element-806" />
                </div>
                <div className="md:w-2/3 p-6 md:p-8" data-id="element-807">
                  <div className="flex items-center gap-3 mb-3" data-id="element-808">
                    <div className="bg-[#b8956a]/10 p-2 rounded-full" data-id="element-809">
                      <Gift className="w-5 h-5 text-[#b8956a]" data-id="element-810" />
                    </div>
                    <span className="text-xs font-bold tracking-wider text-[#c9977e] uppercase bg-[#c9977e]/10 px-3 py-1 rounded-full" data-id="element-811">
                      BONO #4
                    </span>
                  </div>
                  <h3 className="text-xl md:text-2xl font-bold text-[#3d2b1f] mb-2" data-id="element-812">
                    LA LISTA SECRETA: 50 NEGOCIOS "CHAT-DEPENDIENTES"
                  </h3>
                  <div className="flex items-center gap-3 mb-4" data-id="element-813">
                    <span className="bg-[#7a9a6b] text-white text-xs font-bold px-3 py-1 rounded-full" data-id="element-814">
                      INCLUIDO HOY
                    </span>
                    <span className="text-[#9a8b7a] text-xs" data-id="element-815">
                      (Precio regular: $14)
                    </span>
                  </div>
                  <p className="text-[#6b5744] mb-4" data-id="element-816">
                    Los 50 tipos de negocios latinos que están perdiendo ventas
                    porque no tienen a nadie que responda sus mensajes.
                  </p>
                  <ul className="space-y-2 mb-4" data-id="element-817">
                    {['50 tipos de negocios que necesitan atención por chat', 'Checklist "Señales de Oro" para identificar targets en 10 seg', '3 rutas de búsqueda (IG / Google Maps / Facebook)', '12 aperturas ultra cortas para iniciar conversación', 'Plantilla "Mi Lista de 10" para tus primeros targets'].map((item, i) => <li key={i} className="flex items-start gap-2 text-[#3d2b1f]" data-id="element-818">
                        <Check className="w-4 h-4 text-[#7a9a6b] flex-shrink-0 mt-0.5" data-id="element-819" />
                        <span className="font-medium" data-id="element-820">{item}</span>
                      </li>)}
                  </ul>
                  <p className="text-[#c9977e] italic font-medium mt-3" data-id="element-821">
                    "No más parálisis. Solo acción."
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA — bottom of landing */}
      <section className="py-14 md:py-16 px-4 md:px-6 bg-[#faf6f1] text-center" data-id="element-822">
        <div className="flex flex-col items-center justify-center gap-3 max-w-3xl mx-auto" data-id="element-823">
          <a href={getCartUrl()} onClick={handleBottomCTA} className="w-full sm:w-auto px-8 py-4 bg-[#b8956a] hover:bg-[#c9a87a] text-[#3d2b1f] font-bold text-lg rounded-full shadow-lg hover:shadow-xl transition-all transform hover:-translate-y-1 flex items-center justify-center gap-2 group" data-id="element-824">
            Sí, quiero conseguir mi primer cliente — ${totalPrice} USD
          </a>
          <div className="flex items-center gap-2 text-xs md:text-sm text-[#6b5744] font-medium opacity-80" data-id="element-825">
            <Lock className="w-3.5 h-3.5 flex-shrink-0" data-id="element-826" />
            <span data-id="element-827">Acceso inmediato · Garantía 7 días · 100% en español</span>
          </div>
        </div>
      </section>
    </>;
}